<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useFlowBuilderStore } from '../store/index.js';
import CreateFlowModal from '../components/CreateFlowModal.vue';
import FlowMetricsCards from '../components/FlowMetricsCards.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const router = useRouter();
const store = useFlowBuilderStore();
const { accountScopedRoute } = useAccount();

const showCreateModal = ref(false);
const isSaving = ref(false);
const deleteDialogRef = ref(null);
const flowToDelete = ref(null);
const isDeleting = ref(false);
const searchQuery = ref('');

const activeStatus = ref('all');

const TAB_OPTIONS = computed(() => [
  { value: 'all', label: t('FLOW_BUILDER.LIST.TABS.ALL') },
  { value: 'active', label: t('FLOW_BUILDER.LIST.TABS.ACTIVE') },
  { value: 'inactive', label: t('FLOW_BUILDER.LIST.TABS.INACTIVE') },
]);

const tabs = computed(() => {
  const total = store.flows.length;
  const active = store.flows.filter(f => f.active).length;
  const counts = { all: total, active, inactive: total - active };
  return TAB_OPTIONS.value.map(tab => ({
    label: tab.label,
    value: tab.value,
    count: counts[tab.value],
  }));
});

const activeTabIndex = computed(() =>
  Math.max(
    TAB_OPTIONS.value.findIndex(tab => tab.value === activeStatus.value),
    0
  )
);

const handleTabChange = tab => {
  activeStatus.value = tab.value;
};

onMounted(() => {
  store.fetchFlows();
  store.fetchMetrics();
});

const deleteDescription = computed(() =>
  flowToDelete.value
    ? t('FLOW_BUILDER.LIST.DELETE_DIALOG.DESCRIPTION', {
        name: flowToDelete.value.name,
      })
    : ''
);

const filteredFlows = computed(() => {
  return store.flows.filter(flow => {
    const matchesSearch =
      !searchQuery.value ||
      flow.name.toLowerCase().includes(searchQuery.value.toLowerCase());
    const matchesStatus =
      activeStatus.value === 'all' ||
      (activeStatus.value === 'active' && flow.active) ||
      (activeStatus.value === 'inactive' && !flow.active);
    return matchesSearch && matchesStatus;
  });
});

async function onCreate(flowData) {
  isSaving.value = true;
  try {
    const flow = await store.createFlow(flowData);
    showCreateModal.value = false;
    router.push(accountScopedRoute('flow_editor', { flowId: flow.id }));
  } finally {
    isSaving.value = false;
  }
}

async function onToggleActive(flow) {
  await store.toggleActive(flow.id);
  store.fetchMetrics();
}

function onDelete(flow) {
  flowToDelete.value = flow;
  deleteDialogRef.value?.open();
}

async function confirmDelete() {
  if (!flowToDelete.value) return;
  isDeleting.value = true;
  try {
    await store.deleteFlow(flowToDelete.value.id);
    deleteDialogRef.value?.close();
  } finally {
    isDeleting.value = false;
    flowToDelete.value = null;
  }
}

function goToEditor(flow) {
  router.push(accountScopedRoute('flow_editor', { flowId: flow.id }));
}

import { TRIGGER_EVENTS } from '../constants/nodeTypes.js';
import PageEmptyState from 'escaleshop/shared/components/PageEmptyState.vue';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';

const { isCloudFeatureEnabled, isOnChatwootCloud, accountId } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const paywallI18nKey = computed(() =>
  isOnChatwootCloud.value ? 'PAYWALL' : 'ENTERPRISE_PAYWALL'
);
const isFlowEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.FLOW));

const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

const triggerLabel = computed(() => Object.fromEntries(
  TRIGGER_EVENTS.map(e => [e.key, t(e.label)])
));
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1 overflow-hidden">
    <template v-if="!isFlowEnabled">
      <div
        class="flex flex-1 items-center justify-center min-h-[400px] px-6 py-12"
      >
        <BasePaywallModal
          feature-prefix="FLOW"
          :i18n-key="paywallI18nKey"
          :is-super-admin="isSuperAdmin"
          :is-on-chatwoot-cloud="isOnChatwootCloud"
          @upgrade="openBilling"
        />
      </div>
    </template>
    <template v-else>
      <!-- Sticky header -->
      <header class="sticky top-0 z-30 px-6 lg:px-16 bg-n-surface-1">
        <div
          class="flex items-center justify-between w-full py-6 gap-4 mx-auto max-w-7xl"
        >
          <span class="text-xl font-medium truncate text-n-slate-12">{{
            t('FLOW_BUILDER.LIST.TITLE')
          }}</span>

          <div class="flex items-center gap-3 flex-shrink-0">
            <!-- Search -->
            <Input
              v-model="searchQuery"
              type="search"
              :placeholder="t('FLOW_BUILDER.LIST.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 ltr:!pl-8 !py-1',
              ]"
              class="w-56"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2"
                />
              </template>
            </Input>

            <div class="w-px h-4 bg-n-strong" />

            <Button
              size="sm"
              icon="i-lucide-plus"
              @click="showCreateModal = true"
            >
              {{ t('FLOW_BUILDER.LIST.NEW_FLOW') }}
            </Button>
          </div>
        </div>
      </header>

      <!-- Scrollable content -->
      <main class="flex-1 overflow-y-auto scrollbar-custom px-6 lg:px-16">
        <div class="w-full mx-auto max-w-7xl py-4 flex flex-col gap-4">
          <!-- Métricas -->
          <FlowMetricsCards
            :metrics="store.metrics"
            :loading="store.metricsLoading"
          />

          <!-- Tabs -->
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            @tab-changed="handleTabChange"
          />

          <!-- Skeletons -->
          <div
            v-if="store.loading"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-4"
          >
            <div
              v-for="n in 6"
              :key="n"
              class="flex flex-col outline-1 outline outline-n-container -outline-offset-1 rounded-xl bg-n-solid-2 p-4 gap-3"
            >
              <div class="flex items-center justify-between gap-2">
                <div class="h-4 w-32 rounded-md bg-n-alpha-2 animate-pulse" />
                <div class="h-4 w-12 rounded-full bg-n-alpha-2 animate-pulse" />
              </div>
              <div class="h-3 w-24 rounded-md bg-n-alpha-2 animate-pulse" />
              <div class="h-3 w-20 rounded-md bg-n-alpha-2 animate-pulse" />
              <div class="h-px w-full bg-n-container mt-1" />
              <div class="flex gap-2">
                <div class="h-6 w-14 rounded-lg bg-n-alpha-2 animate-pulse" />
                <div class="h-6 w-14 rounded-lg bg-n-alpha-2 animate-pulse" />
              </div>
            </div>
          </div>

          <!-- Empty -->
          <PageEmptyState
            v-else-if="filteredFlows.length === 0"
            icon="i-lucide-workflow"
            :title="
              store.flows.length === 0
                ? t('FLOW_BUILDER.LIST.EMPTY.NO_FLOWS_TITLE')
                : t('FLOW_BUILDER.LIST.EMPTY.NO_RESULTS_TITLE')
            "
            :description="
              store.flows.length === 0
                ? t('FLOW_BUILDER.LIST.EMPTY.NO_FLOWS_DESCRIPTION')
                : t('FLOW_BUILDER.LIST.EMPTY.NO_RESULTS_DESCRIPTION')
            "
          >
            <Button
              v-if="store.flows.length === 0"
              size="sm"
              icon="i-lucide-plus"
              @click="showCreateModal = true"
            >
              {{ t('FLOW_BUILDER.LIST.EMPTY.CREATE_FIRST') }}
            </Button>
          </PageEmptyState>

          <!-- Grid -->
          <div
            v-else
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-4"
          >
            <div
              v-for="flow in filteredFlows"
              :key="flow.id"
              class="flex flex-col outline-1 outline outline-n-container -outline-offset-1 rounded-xl bg-n-solid-2 hover:bg-n-solid-3 transition-colors cursor-pointer"
              @click="goToEditor(flow)"
            >
              <div class="flex flex-col gap-3 p-4">
                <div class="flex items-start justify-between gap-2">
                  <div class="flex items-center gap-2 min-w-0">
                    <span
                      class="text-sm font-semibold text-n-slate-12 truncate"
                      >{{ flow.name }}</span
                    >
                  </div>
                  <!-- Status badge -->
                  <span
                    class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                    :class="
                      flow.active
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-solid-4 text-n-slate-10'
                    "
                  >
                    <span
                      class="size-1.5 rounded-full"
                      :class="flow.active ? 'bg-n-teal-9' : 'bg-n-slate-7'"
                    />
                    {{
                      flow.active
                        ? t('FLOW_BUILDER.COMMON.ACTIVE')
                        : t('FLOW_BUILDER.COMMON.INACTIVE')
                    }}
                  </span>
                </div>

                <div
                  v-if="flow.trigger_event"
                  class="flex items-center gap-1.5 text-xs text-n-slate-10"
                >
                  <span class="i-lucide-zap size-3" />
                  {{ triggerLabel[flow.trigger_event] ?? flow.trigger_event }}
                </div>

                <div class="flex items-center gap-1.5 text-xs text-n-slate-10">
                  <span class="i-lucide-activity size-3" />
                  {{
                    t(
                      'FLOW_BUILDER.LIST.EXECUTIONS_COUNT',
                      { count: flow.executions_count ?? 0 },
                      flow.executions_count ?? 0
                    )
                  }}
                </div>
              </div>

              <div
                class="flex items-center gap-1 px-3 py-2 border-t border-n-container"
                @click.stop
              >
                <button
                  class="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-colors"
                  :class="
                    flow.active
                      ? 'text-n-teal-11 hover:bg-n-teal-3'
                      : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12'
                  "
                  @click="onToggleActive(flow)"
                >
                  <span
                    :class="flow.active ? 'i-lucide-pause' : 'i-lucide-play'"
                    class="size-3"
                  />
                  {{
                    flow.active
                      ? t('FLOW_BUILDER.LIST.ACTIONS.PAUSE')
                      : t('FLOW_BUILDER.LIST.ACTIONS.ACTIVATE')
                  }}
                </button>
                <button
                  class="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                  @click="
                    router.push(
                      accountScopedRoute('flow_executions', { flowId: flow.id })
                    )
                  "
                >
                  <span class="i-lucide-activity size-3" />
                  {{ t('FLOW_BUILDER.LIST.ACTIONS.EXECUTIONS') }}
                </button>
                <button
                  class="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                  @click="
                    router.push(
                      accountScopedRoute('flow_reports', { flowId: flow.id })
                    )
                  "
                >
                  <span class="i-lucide-bar-chart-3 size-3" />
                  {{ t('FLOW_BUILDER.LIST.ACTIONS.REPORTS') }}
                </button>
                <button
                  class="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-n-ruby-11 hover:bg-n-ruby-3 transition-colors ml-auto"
                  @click="onDelete(flow)"
                >
                  <span class="i-lucide-trash-2 size-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <CreateFlowModal
        :show="showCreateModal"
        :is-saving="isSaving"
        @close="showCreateModal = false"
        @create="onCreate"
      />

      <Dialog
        ref="deleteDialogRef"
        type="alert"
        width="sm"
        :title="t('FLOW_BUILDER.LIST.DELETE_DIALOG.TITLE')"
        :description="deleteDescription"
        :confirm-button-label="t('FLOW_BUILDER.LIST.DELETE_DIALOG.CONFIRM')"
        :is-loading="isDeleting"
        @confirm="confirmDelete"
      />
    </template>
  </section>
</template>
